'use client'

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import styles from './contactUs.module.scss'

// --- Validation Schema ---
const XSS_PATTERN = /[<>]|<.*?>|script/gi
const sanitize = (val: string) => val.replace(XSS_PATTERN, '')

const contactUsSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .max(100, 'Name must be 100 characters or less')
    .transform(sanitize),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .max(100, 'Email must be 100 characters or less'),
  phone: yup
    .string()
    .optional()
    .test(
      'phone-format',
      'Please enter a valid phone number',
      (val) => !val || /^[0-9\s\-\(\)\+]{7,20}$/.test(val)
    ),
  comments: yup
    .string()
    .required('Comments are required')
    .min(10, 'Comments must be at least 10 characters')
    .max(2000, 'Comments must be 2000 characters or less')
    .transform(sanitize),
})

type ContactUsFormData = yup.InferType<typeof contactUsSchema>

interface ContactUsFormProps {
  /** Phone prefix from CMS config (e.g. "+1") */
  phonePrefix?: string
  /** Submit button label from CMS config */
  submitLabel?: string
}

const ContactUsForm: React.FC<ContactUsFormProps> = ({
  phonePrefix = '+1',
  submitLabel = 'Submit',
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactUsFormData>({
    resolver: yupResolver(contactUsSchema) as any,
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      comments: '',
    },
  })

  const onSubmit = async (data: ContactUsFormData) => {
    setSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/showroom/api/saveContactUs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: data.name,
          lastName: '',
          email: data.email,
          phone: data.phone ? `${phonePrefix}${data.phone}` : '',
          subject: 'General Inquiry',
          message: data.comments,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to submit form')
      }

      setSubmitStatus('success')
      setSnackbarOpen(true)
      reset()
    } catch (error) {
      console.error('Contact form submission error:', error)
      setSubmitStatus('error')
      setSnackbarOpen(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.formWrapper}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>
        {/* Name */}
        <div className={styles.fieldGroup}>
          <input
            type="text"
            placeholder="Name"
            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
            {...register('name')}
          />
          {errors.name && (
            <span className={styles.errorText}>{errors.name.message}</span>
          )}
        </div>

        {/* Email */}
        <div className={styles.fieldGroup}>
          <input
            type="email"
            placeholder="Email"
            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            {...register('email')}
          />
          {errors.email && (
            <span className={styles.errorText}>{errors.email.message}</span>
          )}
        </div>

        {/* Phone with country code */}
        <div className={styles.fieldGroup}>
          <div className={styles.phoneRow}>
            <div className={styles.countryCode}>
              <span className={styles.flag}>🇺🇸</span>
              <svg className={styles.chevron} width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.phoneDivider} />
            <div className={styles.phoneInputWrapper}>
              <span className={styles.phonePrefix}>Phone</span>
              <input
                type="tel"
                placeholder={phonePrefix}
                className={`${styles.phoneInput} ${errors.phone ? styles.inputError : ''}`}
                {...register('phone')}
              />
            </div>
          </div>
          {errors.phone && (
            <span className={styles.errorText}>{errors.phone.message}</span>
          )}
        </div>

        {/* Comments */}
        <div className={styles.fieldGroup}>
          <textarea
            placeholder="Comments"
            rows={4}
            className={`${styles.textarea} ${errors.comments ? styles.inputError : ''}`}
            {...register('comments')}
          />
          {errors.comments && (
            <span className={styles.errorText}>{errors.comments.message}</span>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className={styles.submitButton}
        >
          {submitting ? (
            <CircularProgress size={22} sx={{ color: '#fff' }} />
          ) : (
            submitLabel
          )}
        </button>
      </form>

      {/* Success / Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={submitStatus === 'success' ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%', fontSize: '14px' }}
        >
          {submitStatus === 'success'
            ? "Thank you! Your message has been sent successfully. We'll get back to you shortly."
            : 'Something went wrong. Please try again later.'}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default ContactUsForm
